import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Table from '../ogComponent/table';
import Spinner from '../ogComponent/spinner';
import axios from 'axios';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import { Switch, Modal, Box, Button, Typography } from '@mui/material';
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import "./attendence.css";

export default function AllAttendance() {
  const [product, setProduct] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [modalOpen, setModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  // Access permissions array from Redux store
  const permissions = useSelector((state) => state.emp.permissions);

  const header = [
    { name: 'si_no', label: '#', active: true },
    { name: 'employee_code', label: 'Emp ID', active: true },
    { name: 'name', label: 'Employee Name', active: true },
    { name: 'date', label: 'Date', active: true },
    { name: 'clock_in', label: 'Check-In', active: true },
    { name: 'clock_out', label: 'Check-Out', active: true },
    { name: 'working_hours', label: 'Wait/Shift', active: true },
    { name: 'action', label: 'Presence', active: permissions.includes("deleteattendanceID") },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const fetchAttendanceAndLeaveData = async (start, end) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');

      const [attendanceResponse, leaveResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API}/api/attendance/attendance/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: start.format('YYYY-MM-DD'),
            end_date: end.format('YYYY-MM-DD'),
          },
        }),
        axios.get(`${process.env.REACT_APP_API}/api/leavemanagement/leaves/filter_by_date/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: start.format('YYYY-MM-DD'),
            end_date: end.format('YYYY-MM-DD'),
          },
        }),
      ]);

      const attendanceData = attendanceResponse.data.map((item, index) => ({
        ...item,
        si_no: index + 1,
        employee_code: item.employee?.employee_code || 'N/A',
        working_hours: item.employee?.working_hours || 'N/A',
        name: (
          <div className="d-flex align-items-center gap-3">
            <img
              src={item.employee?.profile_photo || '/assets/user.png'}
              className="att-cell-img"
              alt="user"
            />
            <span className="att-name-text">{item.employee?.name || 'N/A'}</span>
          </div>
        ),
        date: dayjs(item.date).format('DD MMM YYYY'),
        clock_in: item.clock_in || '--:--',
        clock_out: item.clock_out || '--:--',
        action: permissions.includes("deleteattendanceID") ? (
          <Switch
            color="primary"
            checked={item.attendance_leave_status === 'Present'}
            onClick={(e) => handleSwitchClick(e, item)}
          />
        ) : null,
      }));

      const leaveData = leaveResponse.data.map((item, index) => ({
        ...item,
        si_no: attendanceData.length + index + 1,
        employee_code: item.employee?.employee_code || 'N/A',
        name: (
          <div className="d-flex align-items-center gap-3">
            <img
              src={item.employee?.profile_photo || '/assets/user.png'}
              className="att-cell-img"
              alt="user"
            />
            <span className="att-name-text">{item.employee?.name || 'N/A'}</span>
          </div>
        ),
        date: dayjs(item.start_date).format('DD MMM YYYY'),
        clock_in: <span className="text-danger small fw-bold">ABSENT</span>,
        clock_out: <span className="text-danger small fw-bold">ABSENT</span>,
        action: permissions.includes("deleteattendanceID") ? (
          <Switch
            color="primary"
            checked={item.attendance_leave_status === 'Present'}
            onClick={(e) => handleSwitchClick(e, item)}
          />
        ) : null,
      }));

      setProduct([...attendanceData, ...leaveData]);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissions.includes("showattendanceID")) {
      fetchAttendanceAndLeaveData(startDate, endDate);
    } else {
      setLoading(false);
    }
  }, [permissions]);

  const handleDateChange = (newStartDate, newEndDate) => {
    const s = newStartDate || startDate;
    const e = newEndDate || endDate;
    if (newStartDate) setStartDate(newStartDate);
    if (newEndDate) setEndDate(newEndDate);

    if (permissions.includes("showattendanceID")) {
      fetchAttendanceAndLeaveData(s, e);
    }
  };

  const handleRowClick = (item) => {
    if (permissions.includes("attendancedetailsID") || String(item.employee?.id) === String(userId)) {
      navigate(`/user/dayList/${item?.employee?.id}`);
    }
  };

  const handleSwitchClick = (e, item) => {
    if (permissions.includes("deleteattendanceID")) {
      e.stopPropagation();
      setCurrentItem(item);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setCurrentItem(null);
  };

  const handleModalConfirm = async () => {
    if (!permissions.includes("deleteattendanceID")) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_API}/api/leavemanagement/leaves/toggle_leave_status/`,
        {
          emp_id: currentItem.employee.id,
          date: currentItem.date || currentItem.start_date,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAttendanceAndLeaveData(startDate, endDate);
    } catch (error) {
      console.error('Error updating attendance status:', error);
    } finally {
      handleModalClose();
    }
  };

  return (
    <div className="AttendanceContainer w-100 h-100 px-4 pt-4 pb-5">
      <div className="attendance-header mb-4">
        <h3 className="m-0">Master Attendance</h3>
        <p className="text-muted small mb-0">Monitor and manage daily check-ins and presence across the organization.</p>
      </div>

      <div className="attendance-filters">
        <div className="fw-bold text-main">Filter Logs</div>
        <div className="date-picker-group">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              value={startDate}
              onChange={(newValue) => handleDateChange(newValue, null)}
              slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
            />
            <DatePicker
              label="To"
              value={endDate}
              onChange={(newValue) => handleDateChange(null, newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
            />
          </LocalizationProvider>
        </div>
      </div>

      {loading ? (
        <div className="py-5 text-center"><Spinner /></div>
      ) : permissions.includes("showattendanceID") ? (
        <div className="attendance-table-wrapper">
          {product.length > 0 ? (
            <Table
              header={header}
              product={product}
              onRowClick={handleRowClick}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          ) : (
            <div className="empty-attendance">
              <img src="/assets/file.png" className="empty-img" alt="No data" />
              <p className="fw-semibold text-muted">No attendance logs found for this period.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="alert alert-warning">No permission to view attendance logs.</div>
      )}

      {modalOpen && permissions.includes("deleteattendanceID") && (
        <Modal open={modalOpen} onClose={handleModalClose}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 380,
            bgcolor: 'white',
            boxShadow: 24,
            p: 4,
            borderRadius: '20px',
            textAlign: 'center',
            outline: 'none'
          }}>
            <div className="d-inline-flex p-3 rounded-circle bg-warning bg-opacity-10 mb-3">
              <WarningAmberIcon sx={{ color: "#F59E0B", fontSize: 48 }} />
            </div>
            <Typography variant="h6" className="fw-bold mb-2">
              Confirm Status Change
            </Typography>
            <Typography variant="body2" className="text-muted mb-4">
              {currentItem?.attendance_leave_status === 'Present'
                ? `Are you sure you want to mark ${currentItem?.employee?.name} as On Leave for this day?`
                : `Are you sure you want to mark ${currentItem?.employee?.name} as Present for this day?`}
            </Typography>
            <Box display="flex" gap={2}>
              <Button fullWidth variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }} onClick={handleModalClose}>
                Cancel
              </Button>
              <Button fullWidth variant="contained" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: 'var(--primary-color)', '&:hover': { bgcolor: 'var(--secondary-color)' } }} onClick={handleModalConfirm}>
                Confirm
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </div>
  );
}